/**
 * AES-256-GCM encryption utility for the frontend (Web Crypto API).
 *
 * Used to encrypt sensitive data (e.g. email) before sending it to backend APIs.
 * The backend decrypts using Node.js crypto with the same key.
 *
 * Types and hex helpers are imported from the shared encryption library
 * (@content-platform/shared-utils) to avoid duplication.
 */
import { getConfigEncryptionKey } from './secrets-endpoint.util';
import { hexToBytes, bytesToHex, AES_GCM_ALGORITHM_WEB, AES_GCM_IV_LENGTH, AES_GCM_TAG_LENGTH, } from '@content-platform/shared-utils';
/**
 * Encrypts a plaintext string using AES-256-GCM via the Web Crypto API.
 *
 * @param plaintext - The string to encrypt (e.g. an email address)
 * @param hexKey    - Optional 64-char hex key override. Defaults to the
 *                    environment's config encryption key.
 * @returns Promise resolving to { iv, tag, data } (all hex-encoded)
 */
export async function encryptString(plaintext, hexKey) {
    const keyHex = hexKey || getConfigEncryptionKey();
    if (!keyHex) {
        throw new Error('Encryption key is not configured. Cannot encrypt.');
    }
    const keyBytes = hexToBytes(keyHex);
    const cryptoKey = await crypto.subtle.importKey('raw', keyBytes.buffer, { name: AES_GCM_ALGORITHM_WEB }, false, ['encrypt']);
    const iv = crypto.getRandomValues(new Uint8Array(AES_GCM_IV_LENGTH));
    const encoder = new TextEncoder();
    const plaintextBytes = encoder.encode(plaintext);
    const ciphertextWithTag = await crypto.subtle.encrypt({ name: AES_GCM_ALGORITHM_WEB, iv: iv.buffer }, cryptoKey, plaintextBytes.buffer);
    // Web Crypto appends the auth tag at the end of the ciphertext
    const result = new Uint8Array(ciphertextWithTag);
    const tagStart = result.length - AES_GCM_TAG_LENGTH;
    const ciphertext = result.slice(0, tagStart);
    const tag = result.slice(tagStart);
    return {
        iv: bytesToHex(iv),
        tag: bytesToHex(tag),
        data: bytesToHex(ciphertext),
    };
}
//# sourceMappingURL=aes-encryption.util.js.map